import styles from './helpers.module.scss';

import { addMonths, differenceInCalendarDays } from 'date-fns';
import { format, isBefore, parseISO, setHours, setMilliseconds, setMinutes, setSeconds, startOfDay } from 'date-fns';
import { toCurrency } from '../table-helpers/table-helpers';
import { v4 } from 'uuid';
import { ContractOrderTypesEnum, IContractInfo, IContractItem, IContractLicense, IContractStateType, ILicenseAccessChangeOrder, IOrderItem, IProductGroup, LicenseTypeEnum, OrderStateEnum } from '../common-types/common-types';
import DeniedAccessPage from '../denied-access-page/denied-access-page';
import { Route } from 'react-router-dom';
import { has } from 'immer/dist/internal';


export enum Roles {
  Admin = "Administrator",
  ProductResponsible = "Produktansvarlig",
  Course = "Kurs",
  Sales = "Salg",
  Accounting = "Regnskap",
  User = "Bruker",
  Visitor = "Innsynsbruker"
}
export enum ROLE_ENUM {
  ADMIN = 0,
  CO_ADMIN = 1,
  CONTRACT_RESPONSIBLE = 2,
  LICENSE_COORDINATOR = 3,
  USER = 4,
  INVITED = 5
}

export function prettyPrintXML(xmlString: string) {
  // Parse the XML string into a DOM Document
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, "application/xml");

  // Serialize the DOM Document back to a string with indentation
  const serializer = new XMLSerializer();
  const xmlPretty = serializer.serializeToString(xmlDoc);

  // Use regex to add indentation for better readability
  const formatted = xmlPretty.replace(/(>)(<)(\/*)/g, '$1\r\n$2$3');
  const finalResult = formatted.replace(/(\s*)(<[^>]+>)/g, function (match, p1, p2) {
    return p1 + p2.replace(/[ ]*\n[ ]*/g, ' ');
  });

  return finalResult;
}
/* eslint-disable-next-line */
export function calcItemRecurringPrice(item: IContractItem, recurringDiscountPercent?: number) {
  const baseCost = item.quantity * item.recurringPrice;
  const discountPercent = recurringDiscountPercent !== undefined ? recurringDiscountPercent : item.recurringDiscountPercent || 0;
  return baseCost * ((100 - discountPercent) / 100);
}

export function calcContractItemRecurringPrice(item: IContractItem) {
  return calcRecurringPrice(item.quantity, item.recurringPrice, item.recurringDiscountPercent);
}

export function calcRecurringPrice(quantity: number, price: number, recurringDiscountPercent?: number) {
  const baseCost = quantity * price;
  const discountPercent = recurringDiscountPercent || 0;
  return baseCost * ((100 - discountPercent) / 100);
}

export function calcRecurringDiscount(item: IContractItem) {
  const baseCost = item.quantity * item.recurringPrice;
  const discountPercent = item.recurringDiscountPercent || 0;
  return baseCost * ((discountPercent) / 100);
}

export function calculateItemOneTimePrice(item: IOrderItem) {
  const oneTimeDiscountAmount = item.oneTimeDiscountAmount || 0;
  return calcItemRecurringPrice(item) - oneTimeDiscountAmount;
}

export function calcOneTimeDiscountFromTotalPrice(contract: IContractStateType, item: IOrderItem, newTotalPrice: number) {
  if (item.itemType === LicenseTypeEnum.ONE_TIME) return item.quantity * (item.setupBasePrice || 0) - newTotalPrice;
  const recurringPrice = calcContractItemRecurringPrice(item);
  const fraction = getRemainingContractIntervalFraction(contract.contractInfo, contract.newSaleDate);
  return (recurringPrice * fraction) - newTotalPrice;
}

export function calcRecurringDiscountFromTotalPrice(quantity: number, itemPrice: number, newTotalPrice: number) {
  const priceBeforeDiscount = quantity * itemPrice;
  return 100 - ((newTotalPrice / priceBeforeDiscount) * 100);
}

export function getRemainingContractIntervalFraction(contractInfo: IContractInfo, saleDate?: Date) {
  const interval = contractInfo.invoiceInterval;
  const nextInvoiceDate = contractInfo.nextInvoicingDate;

  const fromDate = saleDate || new Date();
  const lastRenewal = addMonths(nextInvoiceDate, -interval);
  const daysInContract = differenceInCalendarDays(nextInvoiceDate, lastRenewal);
  const daysUntilRenewal = differenceInCalendarDays(nextInvoiceDate, fromDate);
  return daysUntilRenewal / daysInContract;
}

export function calcIntervalFraction(contractInfo: IContractInfo, startDate: Date, endDate: Date) {
  const lastRenewal = addMonths(startDate, -contractInfo.invoiceInterval);
  const daysInContract = differenceInCalendarDays(lastRenewal, contractInfo.nextInvoicingDate);
  const daysInMove = differenceInCalendarDays(startDate, endDate);
  return daysInMove / daysInContract;
}

export function createContractItemFromOrderItem(item: IOrderItem): IContractItem {
  return {
    id: item.id,
    recurringPrice: item.recurringPrice,
    productName: item.productName,
    quantity: item.quantity,
    vismaContractItemId: item.vismaContractItemId,
    recurringDiscountPercent: item.recurringDiscountPercent,
    licenseNumber: item.licenseNumber
  }
}

export function licenseHasChanges(contract: IContractStateType, licenseNumber: number) {
  return !!contract.orderItems.find((i: any) => i.licenseNumber === licenseNumber);
}

export function calcNextInvoiceCost(contractInfo: IContractInfo, item: IOrderItem, oneTimeDiscountAmount?: number, saleDate?: Date) {
  const oneTimeDiscount = oneTimeDiscountAmount || 0;
  if (item.itemType === LicenseTypeEnum.ONE_TIME) return (item.quantity * (item.setupBasePrice || 0)) - oneTimeDiscount;

  const fraction = getRemainingContractIntervalFraction(contractInfo, saleDate);
  const recurringPrice = calcContractItemRecurringPrice(item);
  const totalPrice = (recurringPrice * fraction) - oneTimeDiscount;
  return totalPrice > 0 ? totalPrice : 0;
}

export function isNewLicense(licenseNumber: number) {
  return licenseNumber < 1000;
}

export function isFutureLicense(licenseNumber: number) {
  return licenseNumber === -1;
}
export const getGroup = (contract: IContractStateType, licenseNumber: number) => {
  const group = contract?.groups.find(g => g.licenses.find(l => l.licenseNumber == licenseNumber));
  return group;
}

export const getLicense = (contract: IContractStateType, licenseNumber: number) => {
  const group = getGroup(contract, licenseNumber);
  return group?.licenses.find(l => l.licenseNumber === licenseNumber);
}

export function contractGetNextInvoice(contract: IContractStateType) {
  const costOfTerminated = contract.orderItems
    .filter(i => i.type === ContractOrderTypesEnum.TerminateLicense && isBefore(i.activationDate, new Date(contract.contractInfo.nextInvoicingDate)))
    .reduce((sum, license) => calcContractItemRecurringPrice(license) + sum, 0);

  const costOfNewSeats = contract.orderItems
    .filter(i => i.type === ContractOrderTypesEnum.AddSeats || i.type === ContractOrderTypesEnum.AddProductsToContract)
    .reduce((sum, license) => calcContractItemRecurringPrice(license) + sum, 0);

  const discountChangeLicenses = contract.orderItems.filter(i => i.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent);
  const licenseIds = discountChangeLicenses.map(l => l.licenseId);
  const allLicenses = contract.groups.reduce((licenses: IContractLicense[], group) => licenses.concat(group.licenses), []);
  const allItems = allLicenses.reduce((items: IContractItem[], license) => items.concat(license.items), []);
  const discountedItems = allItems.filter(i => licenseIds.includes(i.id));
  const initialCostOfDiscounted = discountedItems.reduce((sum, item) => calcContractItemRecurringPrice(item) + sum, 0);
  const costAfterDiscount = discountChangeLicenses.reduce((sum, item) => calcContractItemRecurringPrice(item) + sum, 0);
  const costOfDiscountChange = initialCostOfDiscounted - costAfterDiscount;

  const totalCost = contract.nextInvoiceCost + costOfNewSeats - costOfDiscountChange - costOfTerminated;
  return totalCost;
}
export function startOfDayAt7AM(date: Date) {
  const dayStart = startOfDay(date); // Sets to midnight
  const with7AM = setHours(dayStart, 9);
  const finalDate = setMinutes(setSeconds(setMilliseconds(with7AM, 0), 0), 0);
  return finalDate;
}
export function endOfDayAt4PM(date: Date) {
  const dayEnd = startOfDay(date); // Sets to midnight
  const with8PM = setHours(dayEnd, 22);
  const finalDate = setMinutes(setSeconds(setMilliseconds(with8PM, 0), 0), 0);
  return finalDate;
}
export function contractGetRecurringDiscount(contract: IContractStateType) {
  const allLicenses = contract.groups.reduce((licenses: IContractLicense[], group) => licenses.concat(group.licenses), []);
  const allItems = allLicenses.reduce((items: IContractItem[], license) => items.concat(getAllItemsOnLicense(license)), []);
  const initialCostOfDiscounted = allItems.reduce((sum, item) => calcRecurringDiscount(item) + sum, 0);
  let discountChange = 0;
  const terminatedChange = contract.orderItems
    .filter(i => i.type === ContractOrderTypesEnum.TerminateLicense && isBefore(i.activationDate, new Date(contract.contractInfo.nextInvoicingDate)))
    .reduce((sum, license) => calcRecurringDiscount(license) + sum, 0);

  const saleChanges = contract.orderItems
    .filter(i => i.type === ContractOrderTypesEnum.AddSeats || i.type === ContractOrderTypesEnum.AddProductsToContract)
    .reduce((sum, license) => calcRecurringDiscount(license) + sum, 0);

  const discountChangeLicenses = contract.orderItems.filter(i => i.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent);
  const licenseIds = discountChangeLicenses.map(l => l.licenseId);
  const discountedItems = allItems.filter(i => licenseIds.includes(i.id));
  const initialDiscount = discountedItems.reduce((sum, item) => calcRecurringDiscount(item) + sum, 0);
  const newDiscount = discountChangeLicenses.reduce((sum, item) => calcRecurringDiscount(item) + sum, 0);
  discountChange = initialDiscount - newDiscount;

  return (initialCostOfDiscounted + saleChanges - discountChange - terminatedChange);
}

export function getAllItemsOnContract(contract: IContractStateType) {
  return contract.groups.reduce((list: IContractItem[], g) => list.concat(g.licenses.reduce((allItems: IContractItem[], l) => allItems.concat(getAllItemsOnLicense(l)), [])), []);
}

export function getAllItemsOnLicense(license: IContractLicense) {
  return license.modules.reduce((items, m) => items.concat(m.items), license.items);
}

export function SalePriceRenderer(contractInfo: IContractInfo, change: IOrderItem, saleDate?: Date) {
  if (change.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent) return `${change.recurringDiscountPercent}%`;
  if (change.type === ContractOrderTypesEnum.TerminateLicense) return "Avsluttes";
  return <div>{toCurrency(calcNextInvoiceCost(contractInfo, change, change.oneTimeDiscountAmount, saleDate), contractInfo.currency)}</div>;
}

export function getAllSaleChanges(contract: IContractStateType) {
  return contract.orderItems.filter((o: any) => !o.isExisting);
}

export function createBackendOrder(companyNumber: string, items: IOrderItem[], orderType: ContractOrderTypesEnum, contract: IContractStateType) {
  const itemsOfRightType = items.filter(i => i.type === orderType && !i.isExisting);
  if (itemsOfRightType.length === 0) return undefined;
  const numberOfNewLicenses = itemsOfRightType.filter(i => isNewLicense(i.licenseNumber)).length;

  const convertModulesToNewLicenses = (item: IOrderItem, index: number) => {
    const i = { ...item };
    const getParentItem = (item: IOrderItem) => {
      const allLicenses = contract.groups.reduce((licenses: Array<IContractLicense>, g: any) => licenses.concat(g.licenses), []);
      const allIContractItems = allLicenses.reduce((items: IContractItem[], l: any) => items.concat(l.items), []);
      const parentItem = allIContractItems.find((i: any) => i.id === item.parentLicenseId);
      const newParentItem = items.find(i => i.contractItemId === item.parentLicenseId);
      if (!parentItem && !newParentItem) throw (`Fant ikke foreldreItem til ${item.licenseNumber} ${item.isModule}`);
      return parentItem || newParentItem;
    }
    if (item.isModule) {
      const parentId = item.parentLicenseId ? item.parentLicenseId : getParentItem(item)?.id;
      i.licenseNumber = numberOfNewLicenses + index;
      i.parentLicenseId = parentId
      i.parentLicenseNumber = getParentItem(item)?.licenseNumber;
    }
    return i;
  }

  return {
    activationDate: getISODateString(contract.newSaleDate),
    orderDate: getISODateString(),
    companyNumber: companyNumber,
    contractInfo: contract.contractInfo,
    invoiceInfo: contract.invoiceInfo,
    contractNumber: contract.contractNumber,
    orderType: orderType,
    state: OrderStateEnum.Open,
    message: contract.saleMessage,
    reasonForSale: contract.reasonForSale?.value,
    items: itemsOfRightType.map(convertModulesToNewLicenses),
    id: v4()
  }
}

export function getISODateString(date?: Date) {
  const dateToUse = date || new Date();
  return dateToUse.toISOString();
}

export function getISODate(date?: Date | string) {
  let dateToUse;
  if (!date) dateToUse = new Date();
  else if (typeof date === "string") dateToUse = new Date(date);
  else dateToUse = date;
  const year = dateToUse.getFullYear();
  const month = dateToUse.getMonth();
  const day = dateToUse.getDate();
  const hours = 2;
  return new Date(Date.UTC(year, month, day, hours));
}

export function createLicenseAccessOrder(companyNumber: string, contract: IContractStateType, order: ILicenseAccessChangeOrder) {
  if (order.accessChanges.length === 0) return undefined;
  return {
    accessChanges: order.accessChanges,
    changedByCustomer: order.changedByCustomer,
    activationDate: format(contract.newSaleDate || new Date(), "yyyy-MM-dd"),
    orderDate: format(new Date(), "yyyy-MM-dd"),
    companyNumber: companyNumber,
    contractInfo: contract.contractInfo,
    invoiceInfo: contract.invoiceInfo,
    contractNumber: contract.contractNumber,
    state: OrderStateEnum.Open,
    id: v4()
  }
}

export const groupBy = (array: Array<any>, key: string) => {
  return array.reduce((rv, x) => {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
}

export function clone<X>(object: X): X {
  return JSON.parse(JSON.stringify(object));
}

export function flatten(arr: Array<any>): Array<any> {
  return arr.reduce(function (flat, toFlatten) {
    return flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten);
  }, []);
}

export function distinct(arr: Array<any>): Array<any> {
  return [...new Set(arr)];
}

export const checkAccess = (component: any, userRoles: Roles[], requiredRole: Roles) => {
  const { hasAccess } = checkUserAccess(userRoles, requiredRole);
  return hasAccess ? component : <DeniedAccessPage />;
}

export const checkMenuAccess = (component: any, userRoles: Roles[], requiredRole?: Roles) => {
  const { hasAccess } = checkUserAccess(userRoles, requiredRole);
  if (!requiredRole) return component;
  return hasAccess ? component : null;
}

export const CreateRoute = (path: string, component: JSX.Element, userRoles: Roles[], requiredRole: Roles = Roles.Visitor) => {
  const pathParts = path.split('/');
  pathParts.splice(2, 0, ":companyNumber");
  const pathWithCompanyNumber = pathParts.join("/");
  return <>
    <Route path={path} element={checkAccess(component, userRoles, requiredRole)} />
    <Route path={pathWithCompanyNumber} element={checkAccess(component, userRoles, requiredRole)} />
  </>
}

export function checkUserAccess(userRoles: Roles[], requiredAccess: Roles = Roles.User) {
  let hasAccess = false;
  if (userRoles.includes(Roles.Admin)) return { hasAccess: true, noAccessMessage: "" };
  else if (requiredAccess === Roles.Admin) hasAccess = userRoles.includes(Roles.Admin);
  else if (requiredAccess === Roles.ProductResponsible) hasAccess = userRoles.includes(Roles.ProductResponsible);
  else if (requiredAccess === Roles.Course) hasAccess = userRoles.includes(Roles.Course);
  else if (requiredAccess === Roles.Sales) hasAccess = userRoles.includes(Roles.Sales);
  else if (requiredAccess === Roles.Accounting) hasAccess = userRoles.includes(Roles.Accounting);
  else if (requiredAccess === Roles.User) hasAccess = true;
  else hasAccess = true;

  const noAccessMessage = hasAccess ? "" : `Rettighetsnivået ditt er ${userRoles} og må være ${requiredAccess} for tilgang`;
  return { hasAccess, noAccessMessage };
}

/* === START proposal calculations === */

export const calculatePriceToRenewal = (oneTimeDiscountAmount: number, basePricePerSeat: number, numSeats: number) => {
  const discountedPrice = basePricePerSeat * (1 - (oneTimeDiscountAmount || 0) / 100);
  const priceToRenewal = discountedPrice * numSeats;
  return priceToRenewal
};

export const calculateRecurringPrice = (recurringDiscountPercent: number, basePricePerSeat: number, numSeats: number) => {
  const discountedPrice = basePricePerSeat * (1 - (recurringDiscountPercent || 0) / 100);
  const recurringPrice = discountedPrice * numSeats;
  return recurringPrice
};

export const calculateRecurringDiscountFromPrice = (price: number, product: IProductGroup) => {
  const discount = 100 * (1 - (price / (product.recurringPrice * product.newSeats)))
  return Math.max(discount, 0);
}

export const calculateOneTimeDiscountFromPrice = (price: number, product: IProductGroup) => {
  const discount = 100 * (1 - (price / (product.priceToRenewal * product.newSeats)))
  return Math.max(discount, 0);
}

// calculates raw total price to renewal without discounts
export const calculatePriceToRenewalTotal = (products: IProductGroup[]): number => {
  const sum = products.reduce((total, product) => total + product.priceToRenewal * product.newSeats, 0);
  return sum;
}

// calculates raw total recurring price without discounts
export const calculateRecurringPriceTotal = (products: IProductGroup[]): number => {
  const sum = products.reduce((total, product) => total + product.recurringPrice * product.newSeats, 0);
  return sum;
}

/* === END proposal calculations === */